import React, { FC, memo, useCallback, useEffect, useRef, useState } from 'react';
import { Grid, Link, Skeleton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useRoutesHelper } from '../../helpers/routesHelper';
import { EventCarousel } from '../eventCarouselV2/eventCarousel';
import { EventInformationType } from '../../core/models/events';

const useStyles = makeStyles()(() => ({
  gridPopularEvents: {
    marginBottom: 50,
  },
  headingText: {
    textTransform: 'uppercase',
    fontSize: 14,
    lineHeight: '14px',
    cursor: 'default',
  },
  headingTextClickable: {
    textTransform: 'uppercase',
    fontSize: 14,
    lineHeight: '14px',
    display: 'inline-block',
    padding: 0,
    border: 'none',
    background: 'none',
    color: 'inherit',
    cursor: 'pointer',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    textDecorationColor: 'rgba(255,255,255,0.4)',
    '&:hover': {
      textDecoration: 'underline',
      textDecorationColor: 'rgba(255,255,255,0.8)',
      color: 'rgb(178, 178, 178)',
    },
  },
  gridPopular: {
    marginTop: 50,
  },
}));

const PAGE_SIZE = 10;

export const Events: FC<EventPropsType> = memo(({ city, load, title, onTitleClick, reloadDependencyValue, pageSize = PAGE_SIZE }) => {
  const { classes } = useStyles();
  const { getEventRoute } = useRoutesHelper();

  const [events, setEvents] = useState<EventInformationType[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [isLoadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadingMoreRef = useRef(false);

  const eventsCount = 6;

  const loadInitial = async () => {
    setLoading(true);
    setHasMore(true);
    const res = await load(0);
    setEvents(res ?? []);
    setHasMore((res?.length ?? 0) >= pageSize);
    setLoading(false);
  };

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMoreRef.current) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const currentCount = events.length;
    const res = await load(currentCount);
    setEvents((prev) => [...prev, ...(res ?? [])]);
    setHasMore((res?.length ?? 0) >= pageSize);
    setLoadingMore(false);
    loadingMoreRef.current = false;
  }, [hasMore, events.length, load, pageSize]);

  useEffect(() => {
    loadInitial();
  }, [reloadDependencyValue]);

  const getEventsByLocation = (): EventInformationType[] => {
    const filteredEvents = events.filter((event: EventInformationType) => event?.city === city);
    const sortedEvents = [
      ...events.filter((event) => event.city === city),
      ...events.filter((event) => event.city !== city),
    ];

    return filteredEvents.length >= eventsCount ? filteredEvents : sortedEvents;
  };
  const eventsLocation = city && getEventsByLocation();

  return (
    <Grid item lg={12} xs={12} sm={12} md={12} xl={12} className={classes.gridPopularEvents}>
      <div>
        {title && isLoading && (
          <Typography variant="h6" className={classes.headingText}>
            <Skeleton variant="rectangular" width={140} height={25} />
          </Typography>
        )}

        {!isLoading && !onTitleClick && (
          <Typography variant="h6" className={classes.headingText}>
            {city && eventsLocation?.length >= eventsCount ? `${title}, ${city}` : title}
          </Typography>
        )}

        {!isLoading && onTitleClick && (
          <Link component="button" underline="none" className={classes.headingTextClickable} onClick={onTitleClick}>
            <Typography variant="h6" className={classes.headingText} style={{ cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px' }}>
              {city && eventsLocation?.length >= eventsCount ? `${title}, ${city}` : title}
            </Typography>
          </Link>
        )}
      </div>
      <EventCarousel
        events={city ? eventsLocation : events}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        onLoadMore={hasMore ? loadMore : undefined}
        getItemLink={(itemKey) => getEventRoute(itemKey)}
      />
    </Grid>
  );
});

type EventPropsType = {
  load: (skip: number) => Promise<EventInformationType[]>;
  reloadDependencyValue?: any;
  title?: string;
  onTitleClick?: () => void;
  city?: string;
  pageSize?: number;
};
